import React, { useState } from "react";
import {
    Phone,
    MapPin,
    ArrowRight,
    Star,
    Leaf,
    Sprout,
    Axe,
    CheckCircle2,
    Shield,
    Users,
    Award,
    Send,
} from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import FloatingContact from "@/components/FloatingContact";

const iconMap = { trees: Leaf, sprout: Sprout, axe: Axe, leaf: Leaf };

export default function Home() {
    const { content, loading } = useContent();

    if (loading || !content) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-tbv-bg">
                <div className="text-tbv-deep font-display">Loading…</div>
            </div>
        );
    }

    const phoneHref = `tel:${content.phone.replace(/[^+0-9]/g, "")}`;

    return (
        <div className="bg-tbv-bg min-h-screen">
            <Nav />
            <Hero content={content} phoneHref={phoneHref} />
            <Marquee />
            <Services content={content} />
            <About content={content} />
            <Gallery content={content} />
            <Testimonials content={content} />
            <Contact content={content} phoneHref={phoneHref} />
            <Footer />
            <FloatingContact />
        </div>
    );
}

/* ------------------ HERO ------------------ */
const Hero = ({ content, phoneHref }) => (
    <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                    <div className="reveal flex items-center gap-2 mb-6">
                        <span className="w-8 h-px bg-tbv-deep" />
                        <span className="eyebrow">Family-owned nursery · East Bernard, TX</span>
                    </div>
                    <h1 className="reveal delay-1 section-title text-5xl sm:text-6xl lg:text-7xl mb-6">
                        {content.hero_title.split(" ").slice(0, -2).join(" ")}{" "}
                        <span className="text-tbv-deep italic">
                            {content.hero_title.split(" ").slice(-2).join(" ")}
                        </span>
                    </h1>
                    <p className="reveal delay-2 text-lg text-tbv-soft max-w-xl mb-10 leading-relaxed">
                        {content.hero_subtitle}
                    </p>
                    <div className="reveal delay-3 flex flex-wrap items-center gap-4 mb-12">
                        <a href="#contacto" className="btn-primary" data-testid="hero-cta-quote">
                            {content.hero_cta_text}
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <a href={phoneHref} className="btn-secondary" data-testid="hero-cta-call">
                            <Phone className="w-4 h-4" /> {content.phone}
                        </a>
                    </div>
                    <div className="reveal delay-4 flex flex-wrap items-center gap-6 text-sm text-tbv-soft">
                        <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-tbv-deep" />
                            <span>Insured</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-tbv-deep" />
                            <span>15+ years of experience</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-tbv-deep" />
                            <span>Family-owned business</span>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5 reveal delay-2">
                    <div className="relative">
                        {/* Dynamic logo plate */}
                        <div className="relative bg-tbv-ink rounded-[32px] p-10 overflow-hidden">
                            <div className="hero-grain" />
                            <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-tbv-green/20 blur-3xl" />
                            <div className="relative flex flex-col items-center">
                                {content.logo_url ? (
                                    <img
                                        src={content.logo_url}
                                        alt="Three Brothers Vivero"
                                        data-testid="hero-dynamic-logo"
                                        className="w-full max-w-sm aspect-square object-contain"
                                    />
                                ) : (
                                    <div className="font-condensed text-7xl text-white">3B</div>
                                )}
                            </div>
                        </div>
                        {/* Floating contact card */}
                        <div className="absolute -bottom-8 -left-8 bg-white border border-tbv-border rounded-2xl p-5 shadow-xl hidden sm:block">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-tbv-green flex items-center justify-center">
                                    <Phone className="w-5 h-5 text-tbv-dark" />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-tbv-soft font-bold">Call us today</div>
                                    <a href={phoneHref} className="font-display text-lg font-bold text-tbv-ink">
                                        {content.phone}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* ------------------ MARQUEE ------------------ */
const Marquee = () => {
    const items = [
        "Tree Sales",
        "Landscaping",
        "Tree Cutting",
        "Professional Pruning",
        "Yard Design",
        "Sod Installation",
        "Irrigation",
        "Stump Removal",
    ];
    const all = [...items, ...items];
    return (
        <div className="bg-tbv-ink text-white py-6 overflow-hidden border-y border-tbv-dark">
            <div className="marquee-track">
                {all.map((t, i) => (
                    <div key={i} className="flex items-center gap-16 shrink-0">
                        <span className="font-condensed text-2xl tracking-wider">{t}</span>
                        <Leaf className="w-5 h-5 text-tbv-green" />
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ------------------ SERVICES ------------------ */
const Services = ({ content }) => (
    <section id="servicios" className="py-24 lg:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl mb-16">
                <div className="eyebrow mb-4">What we do</div>
                <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl mb-5">
                    Three services, <span className="italic text-tbv-deep">one promise</span>
                </h2>
                <p className="text-tbv-soft text-lg">
                    From a single fruit tree to a complete yard transformation, we cover every need with the same family-grade care.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
                {content.services.map((service, idx) => {
                    const Icon = iconMap[service.icon] || Leaf;
                    const featured = idx === 0;
                    return (
                        <article
                            key={service.id}
                            className={`bento-card group ${featured ? "md:col-span-3 md:row-span-2" : "md:col-span-3"}`}
                            data-testid={`service-card-${idx}`}
                        >
                            <div className={`relative overflow-hidden ${featured ? "h-80 md:h-[420px]" : "h-56"}`}>
                                <img
                                    src={service.image_url}
                                    alt={service.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-tbv-deep" />
                                </div>
                            </div>
                            <div className="p-6 md:p-8">
                                <h3 className="font-display text-2xl md:text-3xl font-bold text-tbv-ink mb-3">
                                    {service.title}
                                </h3>
                                <p className="text-tbv-soft leading-relaxed">{service.description}</p>
                                <a
                                    href="#contacto"
                                    className="mt-5 inline-flex items-center gap-2 text-tbv-deep font-bold text-sm hover:gap-3 transition-all"
                                    data-testid={`service-cta-${idx}`}
                                >
                                    Request this service <ArrowRight className="w-4 h-4" />
                                </a>
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
    </section>
);

/* ------------------ ABOUT ------------------ */
const About = ({ content }) => (
    <section id="nosotros" className="py-24 lg:py-32 bg-tbv-ink text-white relative overflow-hidden" data-testid="about-section">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-tbv-green/10 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                    <div className="eyebrow text-tbv-green mb-4">About Us</div>
                    <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl mb-6 text-white">
                        {content.about_title}
                    </h2>
                    <p className="text-white/70 text-lg leading-relaxed mb-8">{content.about_text}</p>

                    <div className="grid grid-cols-2 gap-6 mb-10">
                        {[
                            { k: "15+", v: "Years of experience" },
                            { k: "500+", v: "Projects completed" },
                            { k: "100%", v: "Satisfaction guaranteed" },
                            { k: "5★", v: "Average rating" },
                        ].map((s) => (
                            <div key={s.v} className="border-l-2 border-tbv-green pl-4">
                                <div className="font-condensed text-4xl text-tbv-green">{s.k}</div>
                                <div className="text-sm text-white/60">{s.v}</div>
                            </div>
                        ))}
                    </div>

                    <ul className="space-y-3">
                        {[
                            "Free, no-obligation quotes",
                            "Safe, certified crew",
                            "Bilingual service (English / Spanish)",
                            "Warranty on every tree we sell",
                        ].map((f) => (
                            <li key={f} className="flex items-center gap-3 text-white/80">
                                <CheckCircle2 className="w-5 h-5 text-tbv-green flex-shrink-0" />
                                {f}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative">
                    <div className="grid grid-cols-2 gap-4">
                        <img
                            src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80"
                            alt="Árboles"
                            className="rounded-2xl h-72 w-full object-cover"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&q=80"
                            alt="Jardín"
                            className="rounded-2xl h-72 w-full object-cover mt-12"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1599629954294-14ea2eb73d8c?w=800&q=80"
                            alt="Paisaje"
                            className="rounded-2xl h-72 w-full object-cover -mt-8"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1631300313270-227604e71ea5?w=800&q=80"
                            alt="Poda"
                            className="rounded-2xl h-72 w-full object-cover mt-4"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* ------------------ GALLERY ------------------ */
const Gallery = ({ content }) => (
    <section id="galeria" className="py-24 lg:py-32" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
                <div>
                    <div className="eyebrow mb-4">Gallery</div>
                    <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl">
                        Our work, <span className="italic text-tbv-deep">in their yards</span>
                    </h2>
                </div>
                <p className="text-tbv-soft max-w-sm">
                    A sample of real projects completed in East Bernard and surrounding areas.
                </p>
            </div>

            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
                {content.gallery.map((g, i) => (
                    <figure
                        key={g.id}
                        className="break-inside-avoid mb-6 group relative overflow-hidden rounded-2xl border border-tbv-border bg-white"
                        data-testid={`gallery-item-${i}`}
                    >
                        <img
                            src={g.url}
                            alt={g.caption || "Trabajo de Three Brothers Vivero"}
                            className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                            loading="lazy"
                        />
                        {g.caption && (
                            <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                {g.caption}
                            </figcaption>
                        )}
                    </figure>
                ))}
            </div>
        </div>
    </section>
);

/* ------------------ TESTIMONIALS ------------------ */
const Testimonials = ({ content }) => (
    <section id="testimonios" className="py-24 lg:py-32 bg-white border-y border-tbv-border" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl mb-16">
                <div className="eyebrow mb-4">Testimonials</div>
                <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl">
                    What our <span className="italic text-tbv-deep">customers say</span>
                </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {content.testimonials.map((t, i) => (
                    <div
                        key={t.id}
                        className="border border-tbv-border rounded-2xl p-7 bg-tbv-bg hover:border-tbv-green transition-colors"
                        data-testid={`testimonial-${i}`}
                    >
                        <div className="flex gap-1 mb-4">
                            {Array.from({ length: t.rating }).map((_, k) => (
                                <Star key={k} className="w-4 h-4 fill-tbv-green text-tbv-green" />
                            ))}
                        </div>
                        <p className="text-tbv-ink leading-relaxed mb-6">&ldquo;{t.text}&rdquo;</p>
                        <div className="pt-4 border-t border-tbv-border">
                            <div className="font-display font-bold text-tbv-ink">{t.name}</div>
                            <div className="text-xs text-tbv-soft uppercase tracking-wider">{t.location}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* ------------------ CONTACT ------------------ */
const Contact = ({ content, phoneHref }) => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
    const [sending, setSending] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.message) {
            toast.error("Please complete name, phone and message.");
            return;
        }
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Thank you! We'll contact you shortly.");
            setForm({ name: "", phone: "", email: "", service: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error sending request.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contacto" className="py-24 lg:py-32" data-testid="contact-section">
            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <div className="eyebrow mb-4">Contact</div>
                        <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl mb-6">
                            Get your <span className="italic text-tbv-deep">free quote</span>
                        </h2>
                        <p className="text-tbv-soft text-lg mb-10">
                            Tell us what you need and we'll get back to you the same day. No obligation, no fine print.
                        </p>

                        <div className="space-y-4">
                            {(content.contacts && content.contacts.length > 0
                                ? content.contacts
                                : [{ id: "main", name: "Llamar / WhatsApp", phone: content.phone, role: "" }]
                            ).map((c, i) => (
                                <a
                                    key={c.id || i}
                                    href={`tel:${c.phone.replace(/[^+0-9]/g, "")}`}
                                    className="flex items-center gap-4 group"
                                    data-testid={`contact-person-${i}`}
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-tbv-ink text-white flex items-center justify-center group-hover:bg-tbv-green group-hover:text-tbv-ink transition-colors">
                                        <Phone className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-tbv-soft">{c.role || "Customer Service"}</div>
                                        <div className="font-display font-bold text-xl text-tbv-ink">{c.name}</div>
                                        <div className="text-tbv-deep font-bold">{c.phone}</div>
                                    </div>
                                </a>
                            ))}
                            <a href={`mailto:${content.email}`} className="flex items-center gap-4 group" data-testid="contact-email-link">
                                <div className="w-14 h-14 rounded-2xl bg-tbv-bg border border-tbv-border flex items-center justify-center group-hover:border-tbv-deep transition-colors">
                                    <Send className="w-5 h-5 text-tbv-deep" />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-tbv-soft">Email</div>
                                    <div className="font-display font-bold text-lg text-tbv-ink break-all">{content.email}</div>
                                </div>
                            </a>
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-tbv-bg border border-tbv-border flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-tbv-deep" />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-tbv-soft">Address</div>
                                    <div className="font-display font-bold text-lg text-tbv-ink">{content.address}</div>
                                    <div className="text-sm text-tbv-soft">{content.service_area}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form
                        onSubmit={submit}
                        className="bg-white border border-tbv-border rounded-3xl p-8 lg:p-10 space-y-5"
                        data-testid="contact-form"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Name *" name="name" value={form.name} onChange={onChange} testid="contact-input-name" />
                            <Field label="Phone *" name="phone" type="tel" value={form.phone} onChange={onChange} testid="contact-input-phone" />
                        </div>
                        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="contact-input-email" />
                        <div>
                            <label className="text-xs uppercase tracking-wider text-tbv-soft font-bold">Service</label>
                            <select
                                name="service"
                                value={form.service}
                                onChange={onChange}
                                data-testid="contact-input-service"
                                className="mt-2 w-full bg-tbv-bg border border-tbv-border rounded-xl px-4 py-3 focus:outline-none focus:border-tbv-deep transition"
                            >
                                <option value="">Choose a service…</option>
                                <option>Tree Sales</option>
                                <option>Landscaping</option>
                                <option>Tree Cutting / Pruning</option>
                                <option>Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-tbv-soft font-bold">Message *</label>
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                rows={4}
                                data-testid="contact-input-message"
                                className="mt-2 w-full bg-tbv-bg border border-tbv-border rounded-xl px-4 py-3 focus:outline-none focus:border-tbv-deep transition resize-none"
                                placeholder="Tell us what you need…"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={sending}
                            className="btn-primary w-full justify-center disabled:opacity-60"
                            data-testid="contact-submit-button"
                        >
                            {sending ? "Sending..." : "Send Request"}
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, name, value, onChange, type = "text", testid }) => (
    <div>
        <label className="text-xs uppercase tracking-wider text-tbv-soft font-bold">{label}</label>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            data-testid={testid}
            className="mt-2 w-full bg-tbv-bg border border-tbv-border rounded-xl px-4 py-3 focus:outline-none focus:border-tbv-deep transition"
        />
    </div>
);
